// Composer Studio — 식별 키를 **공유하는 다른 자리**를 캔버스에서 보여 준다.
//
// 배경: 같은 bindingKey를 두 자리가 쓰면 값이 공유된다. 에디터는 그 사실을 경고 문구로 알려주지만
// ("이미 다른 노드에서 사용 중인 키입니다 — 같은 키는 입력값을 공유합니다") **어느 자리와**
// 공유하는지는 알려주지 않아, 저작자가 찾을 방법이 없었다. 게다가 키 공유는 오류가 아니라
// 의도된 용법이기도 하다(라디오식 상호배타 체크박스 그룹) — 그래서 경고를 강화하는 대신
// 관계를 **보여 주는** 쪽으로 푼다: 식별 키 칸에 포커스가 있는 동안 같은 키를 쓰는 다른
// 노드/셀을 캔버스에 outline으로 강조한다.
//
// ⚠️ 경고 수집기(EditorPage.collectUsedBindingKeys)의 제외 목록을 그대로 쓰지 않는다 — **두 기능은
// 목적이 다르기 때문이다.** 경고는 *중복이 실수인가*를 묻고, 여기는 *어디와 이어져 있나*를 보여
// 준다. 그래서 경고가 오탐을 막으려 빼는 라디오 그룹 멤버(checkedValue 있는 checkbox)를 여기서는
// 빼지 않는다: 의도적으로 공유한 자리일수록 저작자가 눈으로 확인하고 싶은 관계다. 강조는 키 칸에
// 포커스가 있는 동안만 나타나는 관계 표시라(rose 파선 — 처음 teal이었으나 캔버스에서 묻혀 교체,
// 2026-09-03) 더 보여 준다고 오탐이 되지도 않는다.
// → 앞으로 경고 쪽 제외 규칙이 늘어도 이 수집기에 기계적으로 옮겨 오지 말 것. 그 제외가
//   "실수가 아니다"라는 뜻이면, 그건 여기서는 오히려 **보여 줄 이유**다.
//   ⚠️ 이 파일에는 경고 쪽 제외 규칙 하나(makeMutualExclusionCheck — 같은 branch의 다른 슬롯)가
//   함께 산다. 조상 분기 슬롯 경로라는 **같은 재료**를 쓰기 때문이지, 규칙을 공유하기 때문이 아니다.
//   가시화 수집기(collectSharedKeyOwners·collectSharedKeyOwnerDetails)는 그것을 호출하지 않는다.
//
// 순회는 rc의 공용 walkBindings에 위임한다(표 셀·compute 입력·반복 template·분기 슬롯·셀 안
// 중첩 노드까지 훑는 그 순회 그대로). 소유자 id 문자열도 경고 수집기와 같은 규약을 쓴다:
//   `node:<nodeId>` / `cell:<tableId>:<row>:<col>`  ← ComputeSourceHighlightOverlay가 읽는 형식.

import { createContext, useContext, useEffect, useState } from 'react';
import { walkBindings } from '@safetyhub/report-composer';
import type { ComposerNode } from '@safetyhub/shared';
import { cssEscape } from './canvasRect';
import { slotLabel } from './chainOfBranches';

/** 캔버스 오버레이가 rect로 환산할 수 있는 소유자 식별자(`node:…` / `cell:…`). */
export type BindingOwnerId = string;

/** 지금 포커스된 식별 키 칸 — 어떤 키를, 어느 자리에서 편집 중인가. */
export interface SharedKeyFocus {
  key: string;
  /** 편집 중인 자기 자신의 소유자 id — 강조 대상에서 뺀다(선택 링과 겹쳐 보이면 관계가 안 읽힌다). */
  selfOwnerId: BindingOwnerId;
}

/**
 * body에서 bindingKey가 key와 같은 소유자 id 목록(자기 자신 제외, 중복 제거).
 *
 * key가 빈 문자열이면 빈 배열 — 아직 아무것도 안 친 칸이 템플릿의 빈 키 전부를 강조하면
 * 관계가 아니라 소음이다.
 */
export function collectSharedKeyOwners(
  body: ComposerNode[],
  key: string,
  selfOwnerId: BindingOwnerId,
): BindingOwnerId[] {
  if (key === '') return [];
  const out: BindingOwnerId[] = [];
  const seen = new Set<BindingOwnerId>();
  walkBindings(body, (binding, owner) => {
    if (binding.bindingKey !== key) return;
    const ownerId = owner.cell
      ? `cell:${owner.cell.tableId}:${owner.cell.row}:${owner.cell.col}`
      : `node:${owner.nodeId}`;
    if (ownerId === selfOwnerId || seen.has(ownerId)) return;
    seen.add(ownerId);
    out.push(ownerId);
  });
  return out;
}

// ── 겹치는 자리 "목록" ────────────────────────────────────────────────────────────
// 캔버스 강조만으로는 두 경우에 관계가 안 보인다: 매칭이 **뷰포트 밖**이면 강조가 떠도 화면에
// 없고, **비활성 분기 슬롯 안**이면 DOM이 아예 없어 rect를 못 구한다(조용히 생략). 그래서 경고
// 옆에 겹치는 자리를 **글로** 나열하고, 항목에 커서를 올리면 캔버스를 그 자리로 스크롤 + 강조한다.
// 뒤쪽(DOM 부재)은 스크롤로 닿지 않으므로 **클릭**이 슬롯 탭을 그 자리의 경로로 전환해 도달시킨다
// (revealOwner) — hover=파악, click=도달. 둘 다 선택은 바꾸지 않는다.
//
// ⚠️ 아래 순회는 collectSharedKeyOwners(=walkBindings)와 **같은 자리 집합·같은 순서**를 내야 한다.
// 목록과 강조가 갈라지면 "목록엔 있는데 캔버스엔 안 뜬다"가 되어 관계 파악을 되레 방해한다. 직접
// 순회하는 이유는 walkBindings가 **조상 정보를 주지 않기** 때문이다(분기 슬롯 접두 라벨에 필요).
// 두 벌이 드리프트하지 않도록 spec이 "ownerId 목록이 collectSharedKeyOwners와 같다"를 고정한다.

/** 노드 kind 한글명 — 목록 라벨용(팔레트 어휘와 같은 말). kind 추가 시 tsc가 누락을 잡는다. */
const NODE_KIND_LABEL: Record<ComposerNode['kind'], string> = {
  text: '텍스트',
  image: '이미지',
  table: '표',
  rect: '사각형',
  line: '구분선',
  checkbox: '체크박스',
  choiceGroup: '선택 그룹',
  spacer: '여백',
  group: '섹션',
  stack: '겹침',
  repeat: '반복',
  branch: '분기',
  special: '특수 위젯',
};

/** 겹치는 자리 1개 — 목록 UI가 그대로 렌더한다. */
export interface SharedKeyOwnerDetail {
  /** 캔버스 오버레이/스크롤 앵커가 읽는 소유자 id(`node:…` / `cell:…`). */
  ownerId: BindingOwnerId;
  /** 저작자용 한 줄 라벨(조상 분기 슬롯 접두 포함). */
  label: string;
  /**
   * 조상 분기 슬롯 라벨(바깥→안). 비어 있지 않다는 것은 그 자리가 **어떤 슬롯 안**이라는 뜻이라,
   * 캔버스에 없을 때 "다른 분기 슬롯이라 지금은 안 보인다"를 설명하는 근거가 된다.
   */
  branchSlotLabels: string[];
  /**
   * 편집 중인 self와 **동시에 렌더될 수 없는** 자리인가(같은 branch의 다른 슬롯 — 경고 제외
   * makeMutualExclusionCheck와 같은 규칙). 목록이 이 축으로 절을 가른다: 함께 표시되는 자리는
   * 진짜 중복 후보고, 배타 슬롯의 같은 필드는 값만 공유하는 레이아웃 변형이라 의미가 다르다.
   */
  mutuallyExclusive: boolean;
}

/** 표 셀 자리 라벨 — 좌표는 저작자 기준 1-기준으로 보여 준다(스키마는 0-기준). */
function cellDetailLabel(tableId: string, row: number, col: number): string {
  return `표 ${tableId} · ${row + 1}행 ${col + 1}열`;
}

function withSlotPrefix(slotLabels: string[], base: string): string {
  return slotLabels.length > 0 ? `${slotLabels.join(' › ')} › ${base}` : base;
}

/** 분기 슬롯 경로 1칸 — 그 자리가 **어느 branch의 어느 슬롯** 안인가. */
export interface BranchSlotStep {
  branchNodeId: string;
  slotId: string;
}

/**
 * 순회가 방문하는 "자리" 1건. 한 ownerId가 여러 번 방문될 수 있다(셀 binding + 계산 입력,
 * branch의 조건 바인딩 여러 개). bindingKey가 null이면 **바인딩 없는 자리**로, 목록에는 안 뜨지만
 * 슬롯 경로 지도(collectOwnerSlotPaths)에는 들어간다 — 편집 중인 self가 아직 바인딩이 없거나
 * branch 노드 자신(조건 키 컨텍스트)일 때도 경로를 찾을 수 있어야 하기 때문이다.
 */
interface OwnerVisit {
  ownerId: BindingOwnerId;
  bindingKey: string | null;
  /** 슬롯 접두를 붙이기 전 기본 라벨. */
  baseLabel: string;
  slotPath: BranchSlotStep[];
  /** slotPath와 같은 순서의 표시 라벨(캔버스 슬롯 탭과 같은 규칙). */
  slotLabels: string[];
  /**
   * 이 방문이 **노드 자신**이면 그 노드. 셀·분기 조건·계산 입력 방문에는 없다.
   * branch 노드의 슬롯 id 목록을 읽어 slotId→인덱스로 환산하는 데 쓴다(branchSlotSelectionPatch).
   */
  node?: ComposerNode;
}

/**
 * body의 모든 자리를 walkBindings와 **같은 순서**로 방문하되, 조상 분기 슬롯 경로를 함께 넘긴다.
 *
 * walkBindings를 못 쓰는 이유는 그것이 조상 정보를 주지 않기 때문이다 — 슬롯 접두 라벨(목록)과
 * 상호배타 판정(경고)이 둘 다 그 정보를 필요로 한다. 순회가 두 벌이 되지 않게 그 둘은 이 함수
 * 하나만 쓴다. walkBindings와의 자리 집합·순서 패리티는 spec이 고정한다.
 */
function walkOwnerVisits(nodes: ComposerNode[], visit: (v: OwnerVisit) => void): void {
  const walk = (list: ComposerNode[], slotPath: BranchSlotStep[], slotLabels: string[]): void => {
    for (const node of list) {
      visit({
        ownerId: `node:${node.id}`,
        bindingKey: node.binding?.bindingKey ?? null,
        baseLabel: `${NODE_KIND_LABEL[node.kind]} · ${node.id}`,
        slotPath,
        slotLabels,
        node,
      });
      switch (node.kind) {
        case 'group':
        case 'stack':
          walk(node.children, slotPath, slotLabels);
          break;
        case 'repeat':
          walk([node.template], slotPath, slotLabels);
          break;
        case 'branch':
          for (const cb of node.conditionBindings ?? []) {
            visit({
              ownerId: `node:${node.id}`,
              bindingKey: cb.binding.bindingKey,
              baseLabel: `분기 조건 · ${node.id}`,
              slotPath,
              slotLabels,
            });
          }
          node.branches.forEach((slot, i) => {
            walk(
              slot.children,
              [...slotPath, { branchNodeId: node.id, slotId: slot.id }],
              [...slotLabels, slotLabel(slot, i)],
            );
          });
          break;
        case 'table':
          for (const cell of node.cells) {
            const ownerId = `cell:${node.id}:${cell.row}:${cell.col}`;
            const baseLabel = cellDetailLabel(node.id, cell.row, cell.col);
            visit({
              ownerId,
              bindingKey: cell.binding?.bindingKey ?? null,
              baseLabel,
              slotPath,
              slotLabels,
            });
            for (const inp of cell.compute?.inputs ?? []) {
              if (inp.binding) {
                visit({
                  ownerId,
                  bindingKey: inp.binding.bindingKey,
                  baseLabel,
                  slotPath,
                  slotLabels,
                });
              }
            }
            if (cell.content) walk(cell.content, slotPath, slotLabels);
          }
          break;
        default:
          break;
      }
    }
  };
  walk(nodes, [], []);
}

/**
 * body에서 bindingKey가 key와 같은 자리들의 표시 정보(자기 자신 제외, 자리 단위 중복 제거).
 * ownerId 목록은 collectSharedKeyOwners와 동일(순서 포함) — 목록과 캔버스 강조가 같은 자리를 가리킨다.
 */
export function collectSharedKeyOwnerDetails(
  body: ComposerNode[],
  key: string,
  selfOwnerId: BindingOwnerId,
): SharedKeyOwnerDetail[] {
  if (key === '') return [];
  // self 경로는 별도 선행 순회로 얻는다 — 아래 순회에서 self가 owner들보다 늦게 방문될 수 있다.
  const selfSlotPath = collectOwnerSlotPaths(body).get(selfOwnerId) ?? [];
  const out: SharedKeyOwnerDetail[] = [];
  const seen = new Set<BindingOwnerId>();
  walkOwnerVisits(body, (v) => {
    if (v.bindingKey !== key) return;
    if (v.ownerId === selfOwnerId || seen.has(v.ownerId)) return;
    seen.add(v.ownerId);
    out.push({
      ownerId: v.ownerId,
      label: withSlotPrefix(v.slotLabels, v.baseLabel),
      branchSlotLabels: v.slotLabels,
      mutuallyExclusive: isMutuallyExclusiveSlotPath(selfSlotPath, v.slotPath),
    });
  });
  return out;
}

/**
 * 자리(ownerId) → 그 자리의 분기 슬롯 경로(바깥→안) 지도.
 *
 * 바인딩이 없는 노드/셀도 담는다 — 편집 중인 self가 아직 바인딩 전이거나(빈 키) branch 노드
 * 자신(조건 키 컨텍스트)일 수 있고, 그때 self 경로를 못 찾으면 상호배타 판정이 조용히 꺼진다.
 * 같은 ownerId가 여러 번 방문되면 첫 경로만 쓴다(같은 자리의 경로는 항상 같다).
 */
export function collectOwnerSlotPaths(body: ComposerNode[]): Map<BindingOwnerId, BranchSlotStep[]> {
  const out = new Map<BindingOwnerId, BranchSlotStep[]>();
  walkOwnerVisits(body, (v) => {
    if (!out.has(v.ownerId)) out.set(v.ownerId, v.slotPath);
  });
  return out;
}

/**
 * 슬롯 경로(collectOwnerSlotPaths 산출) → 편집 캔버스 슬롯 선택 **patch**(`{ branchNodeId: 슬롯 인덱스 }`).
 *
 * 캔버스의 슬롯 선택 state(EditorPage `activeBranchSlots`)는 branch별 **인덱스**를 들고 있고
 * 슬롯 경로는 **slotId**를 들고 있다 — 그 사이를 여기서 메운다. 경로 순서를 인덱스로 쓰면 안 된다:
 * 경로는 "바깥→안" 깊이 순서이고 인덱스는 그 branch 안 슬롯의 자리라, 두 번째 슬롯을 고른 바깥
 * 분기에서 조용히 다른 슬롯이 열린다(=도달했다고 말하면서 엉뚱한 자리를 보여 준다).
 *
 * 못 찾은 칸(삭제된 branch·바뀐 slotId)은 **담지 않는다** — 그 자리에 -1이나 0을 넣으면 렌더 쪽
 * clamp가 0번 슬롯을 열어, 저작자가 고르지도 않은 슬롯으로 캔버스가 바뀐다.
 */
export function branchSlotSelectionPatch(
  body: ComposerNode[],
  slotPath: BranchSlotStep[],
): Record<string, number> {
  if (slotPath.length === 0) return {};
  const wanted = new Map(slotPath.map((step) => [step.branchNodeId, step.slotId]));
  const patch: Record<string, number> = {};
  walkOwnerVisits(body, (v) => {
    if (!v.node || v.node.kind !== 'branch') return;
    const node = v.node;
    const slotId = wanted.get(node.id);
    if (slotId === undefined || patch[node.id] !== undefined) return;
    const index = node.branches.findIndex((slot) => slot.id === slotId);
    if (index >= 0) patch[node.id] = index;
  });
  return patch;
}

/**
 * 두 자리가 **동시에 렌더될 수 없나** — 어느 한 branch에서 서로 다른 슬롯으로 갈라지면 true.
 *
 * 그런 두 자리의 키 공유는 실수가 아니라 **같은 필드의 레이아웃 변형**이다(단일회차용 제목 칸과
 * 다회차용 제목 칸). 값이 실제로 섞이지 않으므로 중복 경고에서 뺀다 — 라디오 그룹 멤버 제외와
 * 같은 성격이다.
 *
 * 갈라지지 않는 두 경우는 그대로 중복이다: **다른 branch** 노드의 슬롯끼리는 둘 다 렌더될 수 있고,
 * **같은 슬롯** 안 두 자리는 애초에 함께 렌더된다. 중첩 branch는 경로 어느 칸에서 갈라져도 성립.
 */
export function isMutuallyExclusiveSlotPath(a: BranchSlotStep[], b: BranchSlotStep[]): boolean {
  return a.some((stepA) => {
    const stepB = b.find((s) => s.branchNodeId === stepA.branchNodeId);
    return stepB !== undefined && stepB.slotId !== stepA.slotId;
  });
}

/**
 * "이 자리는 편집 중인 self와 동시에 렌더될 수 없나" 판정기 — 중복 경고 집계(EditorPage
 * collectUsedBindingKeys)가 자리마다 이걸 물어 상호배타 소유자를 뺀다.
 *
 * 경로 지도를 한 번만 만들어 재사용하도록 판정기를 돌려준다(자리마다 body를 다시 훑지 않게).
 * 경고 쪽 규칙이라 **가시화 수집기는 이걸 쓰지 않는다** — 위 collectSharedKeyOwners 주석 참조.
 */
export function makeMutualExclusionCheck(
  body: ComposerNode[],
  selfOwnerId: BindingOwnerId,
): (ownerId: BindingOwnerId) => boolean {
  const slotPaths = collectOwnerSlotPaths(body);
  const selfSlotPath = slotPaths.get(selfOwnerId) ?? [];
  return (ownerId) =>
    isMutuallyExclusiveSlotPath(selfSlotPath, slotPaths.get(ownerId) ?? []);
}

/**
 * 소유자 id → 캔버스 앵커 selector(`[data-composer-node-id=…]` / `[data-composer-cell=…]`).
 * 스크롤·"지금 캔버스에 있나" 판정이 같은 selector를 쓰게 한 곳에서 만든다. 형식이 아니면 null.
 * (파싱 규약은 ComputeSourceHighlightOverlay.rectForSourceId와 동형 — tableId엔 ':'가 없다는 전제.)
 */
export function ownerAnchorSelector(ownerId: BindingOwnerId): string | null {
  if (ownerId.startsWith('cell:')) {
    const rest = ownerId.slice('cell:'.length);
    const colSep = rest.lastIndexOf(':');
    if (colSep === -1) return null;
    const beforeCol = rest.slice(0, colSep);
    const rowSep = beforeCol.lastIndexOf(':');
    if (rowSep === -1) return null;
    const tableId = beforeCol.slice(0, rowSep);
    const row = beforeCol.slice(rowSep + 1);
    const col = rest.slice(colSep + 1);
    if (!tableId || row === '' || col === '') return null;
    return `[data-composer-cell="${cssEscape(`${tableId}::${row}::${col}`)}"]`;
  }
  if (ownerId.startsWith('node:')) {
    const nodeId = ownerId.slice('node:'.length);
    return nodeId ? `[data-composer-node-id="${cssEscape(nodeId)}"]` : null;
  }
  return null;
}

/**
 * 그 자리가 **지금 캔버스에 렌더돼 있나** — 비활성 분기 슬롯 안이면 DOM 자체가 없어 false.
 * hover 강조·스크롤이 아무 일도 못 하는 항목을 목록이 미리 흐리게 표시하는 데 쓴다(같은 selector를
 * 쓰므로 판정과 실제 동작이 어긋나지 않는다).
 */
export function isOwnerOnCanvas(ownerId: BindingOwnerId): boolean {
  const selector = ownerAnchorSelector(ownerId);
  return selector ? document.querySelector(selector) !== null : false;
}

/**
 * 포커스 보고 + 목록 조회 채널 — EditorPage가 state setter/조회 함수를 담아 내려보낸다.
 * 참조 안정 필수(훅 effect deps에 들어간다) → EditorPage는 useMemo([])로 고정하고, body 같은
 * 변하는 값은 함수 안에서 ref로 읽는다.
 */
export interface SharedKeyFocusChannel {
  setFocus: (focus: SharedKeyFocus | null) => void;
  /** 목록 항목 hover — 그 자리로 캔버스를 스크롤하고 강조한다. null이면 해제(선택은 불변). */
  setHoverOwner: (ownerId: BindingOwnerId | null) => void;
  /**
   * 목록 항목 클릭 — 그 자리에 **도달**한다: 조상 분기 슬롯 탭을 그 자리의 경로로 전환한 뒤
   * 캔버스를 스크롤하고 1회 플래시한다.
   *
   * hover(파악, 뷰 불변)와 축이 다르다 — hover만으로는 **비활성 슬롯 안** 자리에 닿을 수 없다
   * (DOM이 없어 스크롤도 강조도 조용히 아무 일도 안 한다). 검출은 정적 분석이라 그런 자리까지
   * 잡으므로, 검출된 것은 클릭으로 닿을 수 있어야 한다.
   *
   * **선택(selectSingleNode)은 바꾸지 않는다** — 겹침을 확인하러 온 것이지 편집 대상을 옮기려는
   * 게 아니다(저작 중이던 자리를 잃으면 안 된다). 슬롯 전환 뒤에도 앵커가 없으면(실데이터 반영
   * 중 0행 반복 안) 뷰 모드를 대신 바꾸지 않고 사유만 알린다 — 뷰 모드는 저작자 소유다.
   */
  revealOwner: (ownerId: BindingOwnerId) => void;
  /** 지금 template.body 기준으로 그 키를 공유하는 자리 목록(자기 제외). */
  getOwnerDetails: (key: string, selfOwnerId: BindingOwnerId) => SharedKeyOwnerDetail[];
}

export const SharedKeyFocusContext = createContext<SharedKeyFocusChannel | undefined>(undefined);

/**
 * 식별 키 입력에 그대로 스프레드할 `{ onFocus, onBlur }`.
 *
 * - `selfOwnerId`가 없으면 빈 객체 — 초안/일괄 패널의 가짜 노드처럼 캔버스에 대응 자리가 없는
 *   컨텍스트에서는 강조가 무의미하다(있지도 않은 노드를 가리키게 된다).
 * - 포커스 중 `key`가 바뀌면(=타이핑) 그때마다 다시 보고한다. 포커스 시점 값으로 굳히면
 *   키를 고쳐 가며 "어디와 겹치나"를 보는 동작이 안 된다.
 * - 언마운트(패널 전환·선택 해제)에도 해제된다 — blur가 안 오는 경로가 있다.
 */
export function useSharedKeyFocus(
  selfOwnerId: BindingOwnerId | undefined,
  key: string,
): { onFocus?: () => void; onBlur?: () => void } {
  const channel = useContext(SharedKeyFocusContext);
  const [focused, setFocused] = useState(false);
  const active = focused && !!channel && !!selfOwnerId;

  useEffect(() => {
    if (!channel || !active || !selfOwnerId) return;
    channel.setFocus({ key, selfOwnerId });
    return () => channel.setFocus(null);
  }, [channel, active, key, selfOwnerId]);

  if (!channel || !selfOwnerId) return {};
  return { onFocus: () => setFocused(true), onBlur: () => setFocused(false) };
}
